import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, Check, X, Mail, Activity, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCities } from "@/context/CitiesContext";
import { AdminProviderFeeds } from "@/components/OrganiserFeeds";
import { toast } from "sonner";

const inputCls =
  "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

function CityChips({ cities, selected, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-cities`}>
      {cities.map((c) => {
        const on = selected.includes(c.slug);
        return (
          <button
            key={c.slug}
            type="button"
            data-testid={`${testidPrefix}-city-${c.slug}`}
            onClick={() => onChange(on ? selected.filter((s) => s !== c.slug) : [...selected, c.slug])}
            className={`overline px-3 py-1.5 border transition-colors ${on ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-400"}`}
          >
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

const blank = { email: "", password: "", name: "", cities: [] };

export const OrganiserAccounts = () => {
  const { cities } = useCities();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/organiser-accounts")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password are required");
      return;
    }
    if (!form.cities.length) {
      toast.error("Assign at least one city");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/organiser-accounts", form);
      toast.success("Organiser account created");
      setForm(blank);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <p className="text-sm text-zinc-400 max-w-2xl">
        Trusted organisers log in at <span className="text-ec-volt">/login</span> and manage their own events for the cities you assign.
        Their events publish <span className="text-white">immediately</span> and are tagged so you're notified.
      </p>

      <DigestPanel />

      <AdminProviderFeeds />

      {loading ? (
        <p className="overline text-zinc-500">Loading…</p>
      ) : !items.length ? (
        <div className="border border-white/10 bg-ec-surface p-12 text-center">
          <p className="font-heading text-3xl text-zinc-400">No organiser accounts</p>
          <p className="text-zinc-500 mt-2">Create one below to let a club or venue manage their own fixtures.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="organiser-accounts-list">
          {items.map((a) => (
            <AccountCard key={a.id} account={a} allCities={cities} onChange={load} />
          ))}
        </div>
      )}

      <div>
        <h3 className="font-heading text-3xl tracking-wide mb-4 flex items-center gap-2">
          <UserPlus size={22} className="text-ec-volt" /> Add an Organiser Login
        </h3>
        <form onSubmit={add} className="bg-ec-surface border border-white/10 p-6 space-y-4 max-w-2xl" data-testid="add-organiser-account-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Display name</span>
              <input data-testid="org-acc-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Worcester Warriors" />
            </label>
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Email *</span>
              <input data-testid="org-acc-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="club@example.com" />
            </label>
          </div>
          <label className="block">
            <span className="overline text-zinc-500 mb-2 block">Temporary password *</span>
            <input data-testid="org-acc-password" type="text" value={form.password} onChange={set("password")} className={inputCls} placeholder="Share this with the organiser" />
          </label>
          <label className="block">
            <span className="overline text-zinc-500 mb-2 block">Assigned cities *</span>
            <CityChips cities={cities} selected={form.cities} onChange={(c) => setForm({ ...form, cities: c })} testidPrefix="org-acc-add" />
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="org-acc-submit"
            className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            <UserPlus size={16} /> {saving ? "Creating…" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

function DigestPanel() {
  const [digest, setDigest] = useState(null);
  const [days, setDays] = useState(7);
  const [sending, setSending] = useState(false);

  const load = (d) =>
    api.get("/admin/organiser-digest", { params: { days: d } }).then(({ data }) => setDigest(data)).catch(() => {});
  useEffect(() => { load(days); }, [days]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/organiser-digest/send", null, { params: { days } });
      if (data.digest.total_actions === 0) toast("No organiser activity in this window — nothing to send");
      else toast.success(`Digest sent to ${data.recipients.join(", ")} (${data.digest.total_actions} actions)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!digest) return null;
  const c = digest.counts;

  return (
    <div className="bg-ec-surface border border-white/10 p-6" data-testid="organiser-digest-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="font-heading text-2xl tracking-wide flex items-center gap-2"><Activity size={18} className="text-ec-volt" /> Organiser Activity</h3>
        <div className="flex items-center gap-2">
          <div className="flex border border-white/15" data-testid="digest-range">
            {[7, 14, 30].map((d, i) => (
              <button key={d} data-testid={`digest-days-${d}`} onClick={() => setDays(d)} className={`overline px-3 py-2 transition-colors ${i > 0 ? "border-l border-white/15" : ""} ${days === d ? "bg-ec-volt text-black" : "text-zinc-400 hover:text-white"}`}>{d}d</button>
            ))}
          </div>
          <button data-testid="digest-send" onClick={sendNow} disabled={sending} className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2 hover:border-ec-volt hover:text-ec-volt transition-colors disabled:opacity-50">
            <Send size={14} /> {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-5">
        <DigestStat label="Total actions" value={digest.total_actions} testid="digest-total" accent />
        <DigestStat label="Added" value={c.added} testid="digest-added" />
        <DigestStat label="Updated" value={c.updated} testid="digest-updated" />
        <DigestStat label="Removed" value={c.removed} testid="digest-removed" />
      </div>

      {digest.total_actions === 0 ? (
        <p className="text-zinc-500 text-sm">No organiser activity in the last {digest.period_days} days.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className="overline text-zinc-500 mb-2">By organiser</p>
            <div className="space-y-1.5" data-testid="digest-by-organiser">
              {digest.organisers.map((o) => (
                <div key={o.name} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <span className="font-semibold">{o.name}</span>
                  <span className="text-zinc-500">{o.added}+ · {o.updated}~ · {o.removed}−</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="overline text-zinc-500 mb-2">Recent</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {digest.recent.map((r, i) => (
                <p key={i} className="text-sm text-zinc-400">
                  <span className="text-white">{r.organiser_name}</span> {r.action} <span className="text-zinc-300">{r.event_title}</span> <span className="text-zinc-600 capitalize">· {r.city}</span>
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DigestStat({ label, value, testid, accent }) {
  return (
    <div className="bg-ec-surface p-4" data-testid={testid}>
      <p className="overline text-zinc-500">{label}</p>
      <p className={`font-heading text-4xl leading-none mt-1 ${accent ? "text-ec-volt" : ""}`}>{value}</p>
    </div>
  );
}

function AccountCard({ account, allCities, onChange }) {
  const [cities, setCities] = useState(account.cities || []);
  const [pw, setPw] = useState("");
  const [dirty, setDirty] = useState(false);

  const saveCities = async (next) => {
    setCities(next);
    setDirty(true);
  };

  const persist = async () => {
    try {
      await api.patch(`/admin/organiser-accounts/${account.id}`, { cities, ...(pw ? { password: pw } : {}) });
      toast.success("Account updated");
      setPw("");
      setDirty(false);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async () => {
    try {
      await api.patch(`/admin/organiser-accounts/${account.id}`, { active: !account.active });
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete organiser login "${account.email}"? Their events stay published.`)) return;
    try {
      await api.delete(`/admin/organiser-accounts/${account.id}`);
      toast.success("Account deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`organiser-account-${account.id}`} className="bg-ec-surface border border-white/10 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-heading text-2xl tracking-wide leading-none truncate">{account.name}</h4>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5"><Mail size={12} /> {account.email}</p>
        </div>
        <button
          data-testid={`org-acc-toggle-${account.id}`}
          onClick={toggleActive}
          className={`overline px-3 py-1.5 border transition-colors shrink-0 ${account.active ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-500"}`}
        >
          {account.active ? "ACTIVE" : "DISABLED"}
        </button>
      </div>

      <div>
        <span className="overline text-zinc-500 mb-2 block">Assigned cities</span>
        <CityChips cities={allCities} selected={cities} onChange={saveCities} testidPrefix={`org-acc-edit-${account.id}`} />
      </div>

      <div className="flex items-center gap-2">
        <input
          data-testid={`org-acc-reset-pw-${account.id}`}
          type="text"
          value={pw}
          onChange={(e) => { setPw(e.target.value); setDirty(true); }}
          className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt"
          placeholder="New password (optional)"
        />
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-white/10">
        {dirty && (
          <button data-testid={`org-acc-save-${account.id}`} onClick={persist} className="inline-flex items-center gap-1.5 overline bg-ec-volt text-black px-4 py-2"><Check size={14} /> Save</button>
        )}
        <button data-testid={`org-acc-delete-${account.id}`} onClick={del} className="ml-auto text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}
